import i18n from "i18next";
/**
 * Check document on a phone (Olivia, 10 Oct): the document's errors and
 * warnings, each with the block it belongs to when that is known, so a tap goes
 * to the block. The API's check (/api/latex/:id/validate) returns `issues` with
 * blocks; an older response has only `error` and `warnings`, which still list.
 */
import { validationSignature } from "@/components/editor/validationStatus";
import type { ReaderBlock } from "./mobileReader";

export type IssueSeverity = "error" | "warning";

export interface CheckIssue {
  severity: IssueSeverity;
  /** compile | citation | reference | layout | tikz | frame | latex | structure */
  kind: string;
  message: string;
  blockId?: string | null;
}

export interface CheckResponse {
  valid: boolean;
  error?: string;
  warnings?: string[];
  issues?: CheckIssue[];
}

/** The issues of a check, errors first, then the document's own structure warnings. */
export function toIssues(res: CheckResponse, blocks: readonly ReaderBlock[]): CheckIssue[] {
  const known = new Set(blocks.map((b) => b.id));
  const fromApi: CheckIssue[] = res.issues
    ? res.issues.map((i) => ({ ...i, blockId: i.blockId && known.has(i.blockId) ? i.blockId : null }))
    : [
        ...(res.valid ? [] : [{ severity: "error" as const, kind: "compile", message: firstErrorLine(res.error) }]),
        ...(res.warnings ?? []).map((w) => ({ severity: "warning" as const, kind: "latex", message: w })),
      ];
  const all = [...fromApi, ...structureIssues(blocks)];
  return [...all.filter((i) => i.severity === "error"), ...all.filter((i) => i.severity === "warning")];
}

/**
 * Warnings the blocks show without compiling. A subsection before the first
 * section is numbered 0.1, in the PDF and in the contents: correct LaTeX, but
 * rarely meant, so it is listed rather than hidden (Olivia, 10 Oct).
 */
export function structureIssues(blocks: readonly ReaderBlock[]): CheckIssue[] {
  const out: CheckIssue[] = [];
  let seenTop = false;
  for (const b of blocks) {
    if (b.type !== "heading" || b.content.numbered === false) continue;
    const level = Number(b.content.level ?? 1);
    if (level <= 1) {
      seenTop = true;
      continue;
    }
    if (!seenTop) {
      out.push({
        severity: "warning",
        kind: "structure",
        message: "A subsection comes before the first section, so it is numbered 0.1. Make it a section, or move it after one.",
        blockId: b.id,
      });
      break;
    }
  }
  return out;
}

/** "2 errors · 1 warning", "1 warning", or "No issues". */
export function issueSummary(issues: readonly CheckIssue[]): string {
  const e = issues.filter((i) => i.severity === "error").length;
  const w = issues.length - e;
  const parts = [
    e ? i18n.t("mobile:reader.check.errorCount", { count: e }) : "",
    w ? i18n.t("mobile:reader.check.warningCount", { count: w }) : "",
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : i18n.t("mobile:reader.check.noIssues");
}

/** The "! …" line of a compile failure, without the wrapper. */
export function firstErrorLine(error?: string): string {
  if (!error?.trim()) return "The document does not compile.";
  const bang = error
    .split("\n")
    .map((l) => l.trim())
    .find((l) => l.startsWith("! ") && !l.includes("==> Fatal error"));
  return bang ? bang.slice(2).trim() : error.split("\n")[0].trim();
}

/** A short key for the document as checked: a pass only speaks for that content. */
export function checkKey(documentId: string, blocks: readonly ReaderBlock[]): string {
  const s = validationSignature(blocks);
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return `lilia-check:${documentId}:${(h >>> 0).toString(36)}:${s.length}`;
}
